import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { format, formatDistanceToNow, startOfMonth } from 'date-fns';
import { FileText, Plus, CheckCircle2, Clock, Send, RefreshCw, ChevronDown, ChevronUp, Pencil, X, Save } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────

interface DigestContent {
  summary: string;
  key_wins: string[];
  at_risk: string[];
  activity_highlights: string;
  next_month_focus: string[];
  partner_notes: string;
}

interface Digest {
  id: string;
  org_id: string;
  org_name: string;
  month_year: string;
  digest_content: DigestContent;
  experiments_run: number;
  insights_identified: number;
  published_at: string | null;
  sam_approved_at: string | null;
  created_at: string;
}

interface OrgOption { id: string; name: string; }

// ── Defaults ──────────────────────────────────────────────────────

function emptyContent(): DigestContent {
  return {
    summary: '',
    key_wins: [''],
    at_risk: [''],
    activity_highlights: '',
    next_month_focus: [''],
    partner_notes: '',
  };
}

// ── Main component ────────────────────────────────────────────────

export default function AiaDigestBuilder() {
  const [digests, setDigests]   = useState<Digest[]>([]);
  const [orgs, setOrgs]         = useState<OrgOption[]>([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing]   = useState<Record<string, DigestContent>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  // Create
  const [showCreate, setShowCreate] = useState(false);
  const [newOrgId, setNewOrgId]     = useState('');
  const [newMonth, setNewMonth]     = useState(format(startOfMonth(new Date()), 'yyyy-MM'));
  const [newExps, setNewExps]       = useState('0');
  const [newInsights, setNewInsights] = useState('0');
  const [creating, setCreating]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [digestRes, orgRes] = await Promise.all([
      supabase
        .from('aia_partnership_digest')
        .select('*')
        .order('month_year', { ascending: false }),
      supabase.from('organizations').select('id, name').order('name'),
    ]);
    const orgMap = Object.fromEntries((orgRes.data || []).map(o => [o.id, o.name]));
    setDigests(
      (digestRes.data || []).map(d => ({
        ...d,
        org_name: orgMap[d.org_id] ?? '—',
        digest_content: (d.digest_content as unknown as DigestContent) ?? emptyContent(),
      }))
    );
    setOrgs((orgRes.data || []).map(o => ({ id: o.id, name: o.name })));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id: string) => {
    setExpanded(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };

  const startEdit = (d: Digest) => {
    setEditing(prev => ({ ...prev, [d.id]: JSON.parse(JSON.stringify(d.digest_content)) }));
    setExpanded(prev => { const s = new Set(prev); s.add(d.id); return s; });
  };

  const cancelEdit = (id: string) =>
    setEditing(prev => { const n = { ...prev }; delete n[id]; return n; });

  const updateEditField = (id: string, field: keyof DigestContent, value: unknown) =>
    setEditing(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const updateListItem = (id: string, field: 'key_wins' | 'at_risk' | 'next_month_focus', idx: number, val: string) =>
    setEditing(prev => {
      const arr = [...(prev[id][field] as string[])];
      arr[idx] = val;
      return { ...prev, [id]: { ...prev[id], [field]: arr } };
    });

  const addListItem = (id: string, field: 'key_wins' | 'at_risk' | 'next_month_focus') =>
    setEditing(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: [...(prev[id][field] as string[]), ''] },
    }));

  const removeListItem = (id: string, field: 'key_wins' | 'at_risk' | 'next_month_focus', idx: number) =>
    setEditing(prev => {
      const arr = (prev[id][field] as string[]).filter((_, i) => i !== idx);
      return { ...prev, [id]: { ...prev[id], [field]: arr.length ? arr : [''] } };
    });

  const saveEdit = async (digestId: string, expsVal: number, insightsVal: number) => {
    setSavingId(digestId);
    const content = editing[digestId];
    const { error } = await supabase
      .from('aia_partnership_digest')
      .update({ digest_content: content as unknown as import('@/integrations/supabase/types').Json, experiments_run: expsVal, insights_identified: insightsVal })
      .eq('id', digestId);
    setSavingId(null);
    if (error) { toast.error(error.message); return; }
    toast.success('Digest saved');
    cancelEdit(digestId);
    load();
  };

  const approveDigest = async (id: string) => {
    const { error } = await supabase
      .from('aia_partnership_digest')
      .update({ sam_approved_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Digest approved');
    load();
  };

  const publishDigest = async (id: string) => {
    const { error } = await supabase
      .from('aia_partnership_digest')
      .update({ published_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Digest published');
    load();
  };

  const createDigest = async () => {
    if (!newOrgId || !newMonth) { toast.error('Org and month are required'); return; }
    setCreating(true);
    const { error } = await supabase.from('aia_partnership_digest').insert([{
      org_id: newOrgId,
      month_year: newMonth,
      digest_content: emptyContent() as unknown as import('@/integrations/supabase/types').Json,
      experiments_run: parseInt(newExps) || 0,
      insights_identified: parseInt(newInsights) || 0,
    }]);
    setCreating(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Digest created');
    setShowCreate(false);
    setNewOrgId(''); setNewMonth(format(startOfMonth(new Date()), 'yyyy-MM'));
    setNewExps('0'); setNewInsights('0');
    load();
  };

  const statusBadge = (d: Digest) => {
    if (d.published_at) return <Badge className="bg-green-500/10 text-green-600 border-green-500/30 text-xs"><Send className="h-3 w-3 mr-1" />Published</Badge>;
    if (d.sam_approved_at) return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Approved</Badge>;
    return <Badge variant="outline" className="text-xs"><Clock className="h-3 w-3 mr-1" />Draft</Badge>;
  };

  const ListEditor = ({ id, field, label }: { id: string; field: 'key_wins' | 'at_risk' | 'next_month_focus'; label: string }) => (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {(editing[id][field] as string[]).map((item, idx) => (
        <div key={idx} className="flex gap-1.5">
          <Input
            value={item}
            onChange={e => updateListItem(id, field, idx, e.target.value)}
            className="text-sm"
            placeholder="Enter item…"
          />
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removeListItem(id, field, idx)}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => addListItem(id, field)}>
        <Plus className="h-3 w-3 mr-1" />Add item
      </Button>
    </div>
  );

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">AiA Partnership Digest</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Monthly activity digest per client for the AiA partnership</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />New Digest
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <FileText className="h-5 w-5 animate-pulse mr-2" /><span className="text-sm">Loading digests…</span>
        </div>
      ) : digests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium mb-1">No digests yet</p>
            <p className="text-sm text-muted-foreground mb-4">Create your first monthly partnership digest.</p>
            <Button onClick={() => setShowCreate(true)}><Plus className="h-4 w-4 mr-1.5" />New Digest</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {digests.map(d => {
            const isExpanded = expanded.has(d.id);
            const isEditing  = !!editing[d.id];
            const content    = isEditing ? editing[d.id] : d.digest_content;

            return (
              <Card key={d.id} className="overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/20"
                  onClick={() => !isEditing && toggleExpand(d.id)}
                >
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{d.org_name}</span>
                        <span className="text-muted-foreground text-sm">— {d.month_year}</span>
                        {statusBadge(d)}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {d.experiments_run} experiment{d.experiments_run !== 1 ? 's' : ''} · {d.insights_identified} insight{d.insights_identified !== 1 ? 's' : ''}
                        {' · '}Created {formatDistanceToNow(new Date(d.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  {!isEditing && (
                    isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>

                {(isExpanded || isEditing) && (
                  <div className="border-t border-border/50 px-4 pb-4 pt-3 space-y-4">
                    {isEditing ? (
                      <>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Summary</Label>
                          <Textarea rows={3} value={content.summary} onChange={e => updateEditField(d.id, 'summary', e.target.value)} placeholder="Overall month summary…" />
                        </div>
                        <ListEditor id={d.id} field="key_wins" label="Key wins" />
                        <ListEditor id={d.id} field="at_risk" label="At risk / concerns" />
                        <div className="space-y-1.5">
                          <Label className="text-xs">Activity highlights</Label>
                          <Textarea rows={3} value={content.activity_highlights} onChange={e => updateEditField(d.id, 'activity_highlights', e.target.value)} placeholder="Notable activity this month…" />
                        </div>
                        <ListEditor id={d.id} field="next_month_focus" label="Next month focus" />
                        <div className="space-y-1.5">
                          <Label className="text-xs">Partner notes</Label>
                          <Textarea rows={2} value={content.partner_notes} onChange={e => updateEditField(d.id, 'partner_notes', e.target.value)} placeholder="Notes for the AiA partner…" />
                        </div>
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" onClick={() => saveEdit(d.id, d.experiments_run, d.insights_identified)} disabled={savingId === d.id}>
                            <Save className="h-3.5 w-3.5 mr-1.5" />{savingId === d.id ? 'Saving…' : 'Save'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => cancelEdit(d.id)}>Cancel</Button>
                        </div>
                      </>
                    ) : (
                      <>
                        {content.summary && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Summary</p>
                            <p className="text-sm">{content.summary}</p>
                          </div>
                        )}
                        {content.key_wins?.filter(Boolean).length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Key wins</p>
                            <ul className="space-y-0.5">{content.key_wins.filter(Boolean).map((w, i) => <li key={i} className="text-sm text-muted-foreground flex gap-1.5"><span className="text-green-500">✓</span>{w}</li>)}</ul>
                          </div>
                        )}
                        {content.at_risk?.filter(Boolean).length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">At risk</p>
                            <ul className="space-y-0.5">{content.at_risk.filter(Boolean).map((r, i) => <li key={i} className="text-sm text-muted-foreground flex gap-1.5"><span className="text-yellow-500">⚠</span>{r}</li>)}</ul>
                          </div>
                        )}
                        {content.activity_highlights && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Activity highlights</p>
                            <p className="text-sm text-muted-foreground">{content.activity_highlights}</p>
                          </div>
                        )}
                        {content.next_month_focus?.filter(Boolean).length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Next month focus</p>
                            <ul className="space-y-0.5">{content.next_month_focus.filter(Boolean).map((f, i) => <li key={i} className="text-sm flex gap-1.5"><span className="text-blue-400">→</span>{f}</li>)}</ul>
                          </div>
                        )}
                        {content.partner_notes && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Partner notes</p>
                            <p className="text-sm text-muted-foreground">{content.partner_notes}</p>
                          </div>
                        )}
                        {!content.summary && !content.activity_highlights && (
                          <p className="text-sm text-muted-foreground italic">No content yet. Click Edit to fill in this digest.</p>
                        )}
                        <Separator />
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => startEdit(d)}>
                            <Pencil className="h-3.5 w-3.5 mr-1.5" />Edit
                          </Button>
                          {!d.sam_approved_at && (
                            <Button size="sm" variant="outline" onClick={() => approveDigest(d.id)}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Approve
                            </Button>
                          )}
                          {d.sam_approved_at && !d.published_at && (
                            <Button size="sm" onClick={() => publishDigest(d.id)}>
                              <Send className="h-3.5 w-3.5 mr-1.5" />Publish
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>New Digest</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Client org *</Label>
              <Select value={newOrgId} onValueChange={setNewOrgId}>
                <SelectTrigger><SelectValue placeholder="Select org…" /></SelectTrigger>
                <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Month (YYYY-MM)</Label>
              <Input value={newMonth} onChange={e => setNewMonth(e.target.value)} placeholder="2026-03" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Experiments run</Label>
                <Input type="number" min={0} value={newExps} onChange={e => setNewExps(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Insights identified</Label>
                <Input type="number" min={0} value={newInsights} onChange={e => setNewInsights(e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createDigest} disabled={creating}>{creating ? 'Creating…' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
